import React, { useState, useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import axios from 'axios';
import slugify from 'slugify';
import { baseAPI } from '../../../../config';
import { toast } from 'react-toastify';
import useManagementStore from '../../../../store/managementStore';
import StatusTicks from './components/StatusTicks';
import MessageContent from './components/MessageContent';
import PhoneChangeSeparator from './components/PhoneChangeSeparator';
import PhoneStartSeparator from './components/PhoneStartSeparator';
import PhoneSelector from './components/PhoneSelector';
import EmptyState from './components/EmptyState';
import SendErrorModal from './components/SendErrorModal';
import settingsStore from '../../../../store/settingsStore';
import userStore from '../../../../store/userStore';
import { canCommunicate } from '@claimup/shared/helpers/permissions';
import { getPhoneIdFromDetails, normalizePhoneId } from './utils/phoneHelpers';
import { PaperClipIcon, PhotoIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { uploadFileToS3 } from '../../../../services/s3Service';

/**
 * Sustituye el cuadro de escritura cuando la ventana de 24h de Meta está
 * cerrada. No es un error: es el estado normal de una conversación que se ha
 * enfriado. Se explica el porqué y, cuando lo sabemos, desde cuándo — para que
 * el gestor no crea que algo se ha roto.
 *
 * Debajo se ofrece la salida: las plantillas de reenganche aprobadas para la
 * empresa de este expediente, con la vista previa ya rellenada con sus datos.
 * Mientras Meta no haya aprobado ninguna, se dice en qué punto están en lugar de
 * enseñar un desplegable vacío.
 */
// También lo usa la pestaña «Deudores» de la ficha (docs/contactar-deudor-ar-plan.md).
export const VentanaCerrada = ({ replyWindow, templates, onEnviar, enviando, senderReady, esAdmin }) => {
  const nunca = replyWindow?.reason === 'reply_window_never_opened';
  const desde = replyWindow?.closes_at ? new Date(replyWindow.closes_at) : null;

  const disponibles = (templates || []).filter(t => t.selectable);
  const [elegida, setElegida] = useState('');
  const plantilla = disponibles.find(t => t.key === elegida) || null;

  return (
    <div className='p-3 py-4 bg-amber-50 border-t border-amber-200 rounded-b-lg flex-none'>
      <div className='flex items-start gap-2'>
        <LockClosedIcon className='h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5' />
        <div className='text-sm flex-1 min-w-0'>
          <p className='font-medium text-amber-900'>
            {nunca
              ? 'Este deudor todavía no ha escrito'
              : 'La conversación lleva más de 24 horas parada'}
          </p>
          <p className='text-amber-800 mt-0.5'>
            {nunca
              ? 'WhatsApp solo permite iniciar la conversación con una plantilla aprobada.'
              : 'WhatsApp solo permite escribir libremente durante las 24 horas siguientes a cada mensaje del deudor.'}
            {desde && !nunca && (
              <span className='text-amber-700'>
                {' '}
                Se cerró el {desde.toLocaleDateString('es-ES')} a las{' '}
                {desde.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}.
              </span>
            )}
          </p>
          <p className='text-amber-700 mt-1 text-xs'>
            En cuanto el deudor conteste, vuelves a poder escribir con normalidad.
          </p>

          <SelectorPlantillas
            templates={templates}
            disponibles={disponibles}
            elegida={elegida}
            onElegir={setElegida}
            plantilla={plantilla}
            onEnviar={onEnviar}
            enviando={enviando}
            senderReady={senderReady}
            esAdmin={esAdmin}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Plantillas que se le pueden mandar a este deudor.
 *
 * Cuando no hay ninguna disponible NO se enseña un desplegable vacío: se explica
 * en qué punto están. "Tres en revisión por Meta" y "no hay ninguna" son
 * situaciones muy distintas para quien está delante.
 */
const SelectorPlantillas = ({
  templates,
  disponibles,
  elegida,
  onElegir,
  plantilla,
  onEnviar,
  enviando,
  senderReady,
  esAdmin,
}) => {
  if (templates === null) return null; // aún cargando

  if (disponibles.length === 0) {
    const conEstado = (templates || []).filter(t => t.approval_status !== 'draft');
    return (
      <p className='mt-3 pt-3 border-t border-amber-200 text-xs text-amber-700'>
        {/* Tres motivos distintos, y el que sea cambia quién lo arregla y cómo.
            Juntarlos en «no hay ninguna lista» deja sin salida justo al admin,
            que es el único que puede resolverlo. */}
        {senderReady === false ? (
          <>
            Esta empresa <strong>no tiene ningún número de WhatsApp conectado</strong>, y Meta
            aprueba las plantillas contra su cuenta de negocio. Hasta que conecte un número no
            hay forma de escribirle a este deudor.
          </>
        ) : conEstado.length > 0 ? (
          <>
            Todavía no hay ninguna plantilla lista para esta empresa:{' '}
            {conEstado.map(t => `${t.key} (${t.approval_label.toLowerCase()})`).join(', ')}.
          </>
        ) : (
          <>
            Esta empresa todavía no tiene ninguna plantilla.{' '}
            {esAdmin ? (
              <>
                Se le añaden en <strong>Configuración → Gestión propia → WhatsApp →
                Por cliente</strong>.
              </>
            ) : (
              <>Sin plantillas no hay forma de reactivar la conversación desde aquí.</>
            )}
          </>
        )}
      </p>
    );
  }

  return (
    <div className='mt-3 pt-3 border-t border-amber-200'>
      <label htmlFor='plantilla-reenganche' className='block text-xs font-medium text-amber-900 mb-1'>
        Reactivar la conversación con una plantilla
      </label>
      <select
        id='plantilla-reenganche'
        value={elegida}
        onChange={e => onElegir(e.target.value)}
        className='w-full border border-amber-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-amber-500'
      >
        <option value=''>Elige una plantilla…</option>
        {disponibles.map(t => (
          <option key={t.key} value={t.key}>
            {t.description || t.key}
          </option>
        ))}
      </select>

      {plantilla && (
        <div className='mt-2'>
          {/* Se enseña el mensaje EXACTO que saldría, con los datos de este
              expediente. Una plantilla no se puede editar al vuelo, así que lo
              único que cabe decidir es si es la correcta. */}
          <div className='p-2 bg-white border border-amber-200 rounded-lg text-sm text-slate-700 whitespace-pre-wrap'>
            {plantilla.preview_text}
          </div>
          <p className='mt-1 text-xs text-amber-700'>
            Se enviará exactamente este texto. Las plantillas no se pueden editar:
            Meta las aprueba palabra por palabra.
          </p>
          <button
            type='button'
            onClick={() => onEnviar(plantilla.key)}
            disabled={enviando}
            className='mt-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium disabled:bg-amber-300'
          >
            {enviando ? 'Enviando…' : 'Enviar plantilla'}
          </button>
        </div>
      )}
    </div>
  );
};

const WhatsAppChat = ({ interactions, expedientId, debtorId, expedientType }) => {
  const { user } = userStore();
  // Comunicación por WhatsApp según rol×tipo: operator nunca; gestor/company solo en AR.
  const canComm = canCommunicate(user?.role, expedientType);
  const { addInteraction } = useManagementStore();
  const { maytapiPhones } = settingsStore();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const previousExpedientIdRef = useRef(null);

  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedPhoneId, setSelectedPhoneId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [isForcing, setIsForcing] = useState(false);
  // C2 — números seleccionables del expediente (AR → números de la company;
  // judicial/extrajudicial → pool ClaimUp), resueltos por el backend según rol y
  // tenant, ya con el flag `owned`. null = cargando (fail-closed en UI). NO se
  // cruza con el store de settings (admin-only) para que el gestor los vea.
  const [senderNumbers, setSenderNumbers] = useState(null);
  // Estado de la ventana de 24h de Meta, que viaja en la misma llamada. null =
  // aún no sabemos; se trata como abierta para no bloquear el chat mientras
  // carga (el backend es quien decide de verdad).
  const [replyWindow, setReplyWindow] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!expedientId) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${baseAPI}/expedients/${expedientId}/sender-numbers?channel=whatsapp`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(res => {
        setSenderNumbers(res.data?.data?.numbers || []);
        setReplyWindow(res.data?.data?.reply_window || null);
      })
      .catch(() => setSenderNumbers([]));
  }, [expedientId]);

  // Meta no admite texto libre ni adjuntos si el deudor lleva más de 24h sin
  // escribir. Se bloquea el cuadro ANTES de que el gestor redacte, en vez de
  // dejarle escribir un mensaje que el backend va a rechazar.
  const ventanaCerrada = Boolean(replyWindow && replyWindow.applies && !replyWindow.free_form_allowed);

  // Plantillas de reenganche. Se piden solo cuando hacen falta —con la ventana
  // cerrada—: en una conversación viva no aportan nada y serían una llamada de
  // más en cada expediente que se abre.
  const [templates, setTemplates] = useState(null);
  // `null` mientras no se sepa: enseñar «no tiene número» sin haberlo
  // comprobado sería otra forma de mentir.
  const [senderReady, setSenderReady] = useState(null);
  useEffect(() => {
    if (!expedientId || !ventanaCerrada) return;
    const token = localStorage.getItem('token');
    axios
      .get(`${baseAPI}/kapso/expedients/${expedientId}/templates`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then(res => {
        setTemplates(res.data?.data?.templates || []);
        setSenderReady(res.data?.data?.sender_ready ?? null);
      })
      .catch(() => setTemplates([]));
  }, [expedientId, ventanaCerrada]);

  // Números permitidos, ordenados empresa-primero. Mientras carga (null) no se
  // muestra ninguno.
  const scopedPhones = useMemo(
    () => (senderNumbers ? [...senderNumbers].sort((a, b) => Number(b.owned) - Number(a.owned)) : []),
    [senderNumbers]
  );

  // Resuelve el nombre de un phone_id para el historial: primero en el pool
  // permitido, luego en el store (admin), y si no, el propio id.
  const resolvePhoneName = phoneId => {
    if (!phoneId) return phoneId;
    const inScope = (senderNumbers || []).find(p => String(p.phone_id) === String(phoneId));
    if (inScope) return inScope.name || inScope.phone_number || phoneId;
    const inStore = maytapiPhones.find(p => String(p.phone_id) === String(phoneId));
    return inStore?.name || phoneId;
  };

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [interactions]);

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  // Set default phone when component mounts, phones are available, or expedient changes
  // Select the phone used in the last WhatsApp message (sent or received), or first active phone if no messages
  useEffect(() => {
    // Aún cargando el pool → no tocamos la selección.
    if (senderNumbers === null) return;
    // `scopedPhones` ya son los números permitidos (activos), empresa-primero.
    const activePhones = scopedPhones;
    const expedientChanged = previousExpedientIdRef.current !== expedientId;

    // Update phone selection when expedient changes or when no phone is selected
    if (expedientChanged || !selectedPhoneId) {
      // Find the last WhatsApp message (sent or received) to get the phone_id used
      const whatsappMessages = interactions
        .filter(
          interaction =>
            (interaction.type === 'whatsapp_sent' || interaction.type === 'whatsapp_response') &&
            getPhoneIdFromDetails(interaction.details)
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      // Reusar el número de la última interacción SOLO si sigue en el pool.
      const lastMessagePhoneId =
        whatsappMessages.length > 0 ? getPhoneIdFromDetails(whatsappMessages[0].details) : null;
      const lastUsedPhone = lastMessagePhoneId
        ? activePhones.find(phone => phone.phone_id === lastMessagePhoneId)
        : null;

      if (lastUsedPhone) {
        setSelectedPhoneId(lastUsedPhone.phone_id);
      } else if (activePhones.length > 0) {
        setSelectedPhoneId(activePhones[0].phone_id);
      } else {
        // Fail-closed: sin número permitido no hay selección.
        setSelectedPhoneId('');
      }

      // Update the previous expedient ID reference
      previousExpedientIdRef.current = expedientId;
    } else {
      // Check if currently selected phone is still active and in scope
      const selectedPhone = activePhones.find(phone => phone.phone_id === selectedPhoneId);
      if (!selectedPhone) {
        setSelectedPhoneId(activePhones.length > 0 ? activePhones[0].phone_id : '');
      }
    }
  }, [senderNumbers, scopedPhones, selectedPhoneId, interactions, expedientId]);

  const getFileExtension = filename => {
    return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 1);
  };

  const isImageFile = file => {
    return file.type.startsWith('image/');
  };

  const validateAndSetFile = file => {
    if (!file) return false;

    // Check file size (max 16MB for WhatsApp)
    const maxSize = 16 * 1024 * 1024; // 16MB
    if (file.size > maxSize) {
      toast.error('El archivo es demasiado grande. El tamaño máximo es 16MB');
      return false;
    }
    setSelectedFile(file);
    return true;
  };

  const handleFileSelect = e => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
      // Clear the input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
  };

  const handleFileUpload = async file => {
    // Get extension and filename without extension
    const extension = '.' + getFileExtension(file.name);
    const filenameWithoutExt = file.name.slice(0, file.name.lastIndexOf('.')) || file.name;
    const timestamp = Date.now();
    const slug = `whatsapp-${timestamp}-${slugify(filenameWithoutExt, { lower: true, strict: true })}${extension}`;
    // Include whatsapp/sent folder in the path for sent files
    const slugWithFolder = `whatsapp/sent/${slug}`;

    // Upload file to S3
    const uploadResult = await uploadFileToS3(file, expedientId, slugWithFolder, file.name, file.type);

    // Return the S3 key for the backend to generate signed URL
    // The backend will generate a signed URL with long expiration
    return { url: uploadResult.s3Key, filename: file.name, slug };
  };

  const handleKeyDown = e => {
    // If Enter is pressed without Shift, submit the form
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
    // If Shift+Enter is pressed, allow default behavior (new line)
  };

  /**
   * Enviar una plantilla aprobada. Va a un endpoint aparte de
   * `/actions/sendwhatsapp` a propósito: allí la ventana de 24 h es un muro sin
   * excepciones, y esto es lo único que Meta permite cuando está cerrada.
   *
   * El teléfono del deudor no se manda: lo resuelve el backend.
   */
  const [isSendingTemplate, setIsSendingTemplate] = useState(false);
  const handleSendTemplate = async key => {
    setIsSendingTemplate(true);
    try {
      const { data } = await axios.post(
        `${baseAPI}/kapso/expedients/${expedientId}/send-template`,
        { key, ...(debtorId ? { debtor_id: debtorId } : {}) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Plantilla enviada');
      // Se recarga el hilo: la ventana de 24 h NO se abre con esto —la abre la
      // respuesta del deudor—, así que el cuadro de escribir sigue bloqueado y
      // decirlo de otra forma sería mentir.
      if (data?.data?.interaction_id) {
        addInteraction(expedientId, {
          _id: data.data.interaction_id,
          interaction_id: data.data.interaction_id,
          expedient_id: expedientId,
          direction: 'outgoing',
          type: 'whatsapp_sent',
          status: 'sent',
          date: new Date().toISOString(),
          details: {
            type: 'text',
            message: data.data.text,
            phone_id: data.data.used_phone_id,
            provider: 'kapso',
            template_key: data.data.key,
          },
        });
      }
    } catch (error) {
      // El backend explica por qué se niega —aprobación desfasada, falta un dato
      // del expediente, sin número conectado—; un genérico no ayudaría a nadie.
      toast.error(
        error?.response?.data?.error?.message || 'No se ha podido enviar la plantilla'
      );
    } finally {
      setIsSendingTemplate(false);
    }
  };

  const lastPayloadRef = useRef(null);

  const postSendWhatsApp = async (payload, { force = false } = {}) => {
    const response = await axios.post(
      `${baseAPI}/actions/sendwhatsapp`,
      { ...payload, force: force || undefined },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );

    if (response.data?.phone_switched && response.data.used_phone_id) {
      toast.info(
        `Mensaje enviado desde ${resolvePhoneName(response.data.used_phone_id)} (teléfono cambiado automáticamente)`
      );
    }

    const usedPhoneId = response.data?.used_phone_id || response.data?.details?.phone_id;
    if (usedPhoneId && scopedPhones.some(phone => String(phone.phone_id) === String(usedPhoneId))) {
      setSelectedPhoneId(usedPhoneId);
    }

    addInteraction(expedientId, response.data);
    setMessage('');
    setSelectedFile(null);
    lastPayloadRef.current = null;
  };

  const buildErrorFromResponse = error => {
    const data = error?.response?.data;
    if (data && (data.warning || data.reason || data.error)) {
      return {
        description: data.warning || data.reason || data.error,
        canForce: data.can_force === true,
        nextAvailableAt: data.next_available_at || null,
      };
    }
    return {
      description: error?.message || 'Error al enviar el WhatsApp',
      canForce: false,
      nextAvailableAt: null,
    };
  };

  const handleSend = async e => {
    e?.preventDefault?.();

    if (!message.trim() && !selectedFile) {
      return;
    }

    if (!expedientId || !debtorId) {
      toast.error('Faltan identificadores para enviar el WhatsApp');
      return;
    }
    if (!selectedPhoneId) {
      toast.error('Debes seleccionar un teléfono para enviar el mensaje');
      return;
    }

    setIsSending(true);

    try {
      const selectedPhone = scopedPhones.find(phone => phone.phone_id === selectedPhoneId);
      let fileUrl = null;
      let fileType = null;
      let filename = null;

      if (selectedFile) {
        setIsUploadingFile(true);
        try {
          const uploadResult = await handleFileUpload(selectedFile);
          fileUrl = uploadResult.url;
          filename = uploadResult.filename;
          fileType = isImageFile(selectedFile) ? 'image' : 'document';
        } catch (uploadError) {
          setIsUploadingFile(false);
          setIsSending(false);
          setSendError({
            description: 'No se ha podido subir el archivo adjunto. Inténtalo de nuevo.',
            canForce: false,
            nextAvailableAt: null,
          });
          console.error('Error uploading file:', uploadError);
          return;
        }
        setIsUploadingFile(false);
      }

      const payload = {
        expedientId,
        debtorId,
        message: message.trim() || null,
        phone_id: selectedPhone?.phone_id,
        fileUrl: fileUrl || null,
        fileType: fileType || null,
        filename: filename || null,
        caption: message.trim() || null,
      };
      lastPayloadRef.current = payload;

      await postSendWhatsApp(payload);
    } catch (error) {
      console.error('Error sending WhatsApp', error);
      // La ventana pudo cerrarse con la pestaña abierta: el estado que trajimos
      // al montar ya no vale. En vez de dejar un aviso flotando sobre un cuadro
      // que sigue invitando a escribir, se pasa al estado bloqueado.
      const data = error?.response?.data;
      if (data?.error === 'reply_window_closed') {
        setReplyWindow({
          applies: true,
          free_form_allowed: false,
          reason: data.reason || null,
          last_inbound_at: data.last_inbound_at || null,
          closes_at: data.window_closed_at || null,
        });
        return;
      }
      setSendError(buildErrorFromResponse(error));
    } finally {
      setIsSending(false);
      setIsUploadingFile(false);
    }
  };

  const handleForceSend = async () => {
    if (!lastPayloadRef.current) return;
    setIsForcing(true);
    try {
      await postSendWhatsApp(lastPayloadRef.current, { force: true });
      setSendError(null);
    } catch (error) {
      console.error('Error forcing WhatsApp send', error);
      setSendError(buildErrorFromResponse(error));
    } finally {
      setIsForcing(false);
    }
  };

  const handleCloseSendError = () => {
    if (isForcing) return;
    setSendError(null);
  };

  return (
    <div
      className='h-full max-h-[calc(100vh-315px)] bg-gray-50 flex flex-col overflow-hidden min-h-0 relative'
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {isDragging && (
        <div className='absolute inset-0 bg-green-100 bg-opacity-90 z-50 flex items-center justify-center border-4 border-dashed border-green-500 rounded-lg'>
          <div className='text-center'>
            <PhotoIcon className='h-16 w-16 text-green-600 mx-auto mb-4' />
            <p className='text-lg font-semibold text-green-700'>Suelta el archivo aquí para enviarlo</p>
            <p className='text-sm text-green-600 mt-2'>Imágenes, documentos y más</p>
          </div>
        </div>
      )}

      {/* Chat Header */}
      <div className='bg-white border-b border-slate-200 p-3 flex items-center justify-between flex-none'>
        <div className='flex items-center gap-3'>
          <div>
            <h3 className='font-semibold text-slate-900 text-sm'>WhatsApp</h3>
            <p className='text-xs text-slate-500'>Conversación</p>
          </div>
        </div>

        {/* Phone Selector */}
        <PhoneSelector phones={scopedPhones} selectedPhoneId={selectedPhoneId} onPhoneChange={setSelectedPhoneId} />
      </div>

      {/* Chat Messages
          `flex-1` + `min-h-0`, y nada más: la lista se queda con lo que sobre y
          hace scroll.

          Llevaba además un `min-h-[calc(100vh-435px)]` que la obligaba a ocupar
          120 px menos que el contenedor —que es `max-h-[calc(100vh-315px)]` y
          `overflow-hidden`—, así que al pie solo le quedaban esos 120 px.

          Con la caja de texto cabía, porque es pequeña. Con el bloque de
          plantilla —aviso, desplegable y botón— no, y el `overflow-hidden` lo
          RECORTABA sin dejar rastro: se veía el aviso de «este deudor todavía no
          ha escrito» y debajo nada, como si no hubiera ninguna plantilla. */}
      <div
        ref={messagesContainerRef}
        className='flex-1 p-4 space-y-3 overflow-y-auto min-h-0'
      >
        {interactions.length === 0 ? (
          <EmptyState />
        ) : (
          (() => {
            const sortedInteractions = [...interactions].sort((a, b) => new Date(a.date) - new Date(b.date));
            return sortedInteractions.map((interaction, index) => {
              const currentDate = moment(interaction.date).format('DD/MM/YYYY');
              const previousDate = index > 0 ? moment(sortedInteractions[index - 1].date).format('DD/MM/YYYY') : null;
              const showDate = currentDate !== previousDate;

              // Check if phone changed (for both outgoing and incoming messages)
              const currentPhoneId = getPhoneIdFromDetails(interaction.details);

              // Find the last WhatsApp message (sent OR received) before the current one
              let previousWhatsAppInteraction = null;
              if (index > 0) {
                for (let i = index - 1; i >= 0; i--) {
                  const prevInteraction = sortedInteractions[i];
                  if (prevInteraction.type === 'whatsapp_sent' || prevInteraction.type === 'whatsapp_response') {
                    previousWhatsAppInteraction = prevInteraction;
                    break;
                  }
                }
              }

              const previousPhoneId = previousWhatsAppInteraction
                ? getPhoneIdFromDetails(previousWhatsAppInteraction.details)
                : null;

              // Normalize phone IDs to strings for comparison
              const normalizedCurrentPhoneId = normalizePhoneId(currentPhoneId);
              const normalizedPreviousPhoneId = normalizePhoneId(previousPhoneId);

              // Show phone change if:
              // 1. Current message is WhatsApp (sent or received)
              // 2. There's a previous WhatsApp message
              // 3. Both have phone_id
              // 4. Phone IDs are different
              const showPhoneChange =
                (interaction.type === 'whatsapp_sent' || interaction.type === 'whatsapp_response') &&
                previousWhatsAppInteraction !== null &&
                normalizedCurrentPhoneId &&
                normalizedPreviousPhoneId &&
                normalizedCurrentPhoneId !== normalizedPreviousPhoneId;

              // Show the STARTING phone before the first WhatsApp message of the
              // conversation (no previous WhatsApp interaction).
              const showStartPhone =
                (interaction.type === 'whatsapp_sent' || interaction.type === 'whatsapp_response') &&
                previousWhatsAppInteraction === null &&
                Boolean(normalizedCurrentPhoneId);

              // Get phone names for display (use normalized IDs for lookup)
              const previousPhoneName = normalizedPreviousPhoneId
                ? resolvePhoneName(normalizedPreviousPhoneId)
                : null;
              const currentPhoneName = normalizedCurrentPhoneId
                ? resolvePhoneName(normalizedCurrentPhoneId)
                : null;

              return (
                <div key={`${interaction.interaction_id}-${index}`}>
                  {/* Date Separator */}
                  {showDate && (
                    <div className='flex justify-center my-4'>
                      <div className='bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full'>{currentDate}</div>
                    </div>
                  )}

                  {/* Starting phone (first WhatsApp message of the conversation) */}
                  {showStartPhone && <PhoneStartSeparator phoneName={currentPhoneName} />}

                  {/* Phone Change Separator */}
                  {showPhoneChange && (
                    <PhoneChangeSeparator previousPhoneName={previousPhoneName} currentPhoneName={currentPhoneName} />
                  )}

                  {/* Message */}
                  <div className={`flex ${interaction.type === 'whatsapp_sent' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-xs px-4 py-2 rounded-lg flex gap-2 items-start ${
                        interaction.type === 'whatsapp_sent'
                          ? 'bg-green-200 text-gray-800 rounded-br-sm'
                          : 'bg-white text-gray-800 rounded-bl-sm shadow-sm'
                      }`}
                    >
                      <div className='flex-1 min-w-0'>
                        <MessageContent details={interaction.details} />
                      </div>

                      <StatusTicks interaction={interaction} />
                    </div>
                  </div>
                </div>
              );
            });
          })()
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Footer */}
      {!canComm ? (
        <div className='p-3 py-4 bg-white border-t border-gray-200 rounded-b-lg text-center text-sm text-slate-500 flex-none'>
          No tienes permiso para enviar mensajes en este expediente.
        </div>
      ) : ventanaCerrada ? (
        <VentanaCerrada
          replyWindow={replyWindow}
          templates={templates}
          onEnviar={handleSendTemplate}
          enviando={isSendingTemplate}
          senderReady={senderReady}
          esAdmin={user?.role === "admin"}
        />
      ) : (
      <form onSubmit={handleSend} className='p-3 py-2 bg-white border-t border-gray-200 rounded-b-lg flex-none'>
        {/* Selected File Preview */}
        {selectedFile && (
          <div className='mb-2 p-2 bg-gray-100 rounded-lg flex items-center justify-between'>
            <div className='flex items-center gap-2 flex-1 min-w-0'>
              {isImageFile(selectedFile) ? (
                <PhotoIcon className='h-5 w-5 text-gray-600 flex-shrink-0' />
              ) : (
                <PaperClipIcon className='h-5 w-5 text-gray-600 flex-shrink-0' />
              )}
              <span className='text-sm text-gray-700 truncate' title={selectedFile.name}>
                {selectedFile.name}
              </span>
              <span className='text-xs text-gray-500 flex-shrink-0'>
                ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
            <button
              type='button'
              onClick={removeSelectedFile}
              className='text-red-500 hover:text-red-700 text-sm font-medium ml-2'
            >
              Eliminar
            </button>
          </div>
        )}

        <div className='flex items-center gap-2'>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type='file'
            id='whatsapp-file-input'
            className='hidden'
            onChange={handleFileSelect}
            accept='image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt'
          />

          {/* File selection button */}
          <label
            htmlFor='whatsapp-file-input'
            className='p-2 text-gray-600 hover:text-green-600 cursor-pointer flex-shrink-0'
            title='Adjuntar archivo'
          >
            <PaperClipIcon className='h-5 w-5' />
          </label>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Escribe un mensaje...'
            rows={1}
            className='flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500 resize-none overflow-hidden min-h-[38px] max-h-[120px]'
          />
          <button
            type='submit'
            disabled={isSending || isUploadingFile || (!message.trim() && !selectedFile)}
            className={`px-3 py-2 rounded-lg text-white text-sm ${
              isSending || isUploadingFile || (!message.trim() && !selectedFile)
                ? 'bg-green-300 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
            title='Enviar WhatsApp'
          >
            {isUploadingFile ? 'Subiendo...' : isSending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
      )}

      <SendErrorModal
        isOpen={!!sendError}
        onClose={handleCloseSendError}
        error={sendError}
        onForce={handleForceSend}
        isForcing={isForcing}
      />
    </div>
  );
};

export default WhatsAppChat;
